import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Text,
  HStack,
  IconButton,
  Select,
  Input,
  Divider,
} from '@chakra-ui/react';
import { ValueRefPicker } from './ValueRefPicker';
import type { ValueRefTask } from './valueRefTasks';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { MdAdd, MdDelete } from 'react-icons/md';
import { TaskType, TaskFormData } from '@/lib/sdk/chatbot/types';

interface StartnewConfigSectionProps {
  control: Control<TaskFormData>;
  existingTasks: { id: string; name: string; index: number; type: string }[];
  attributeMappingEntries: { key: string; value: string }[];
  /** Tasks the attribute mapping's value picker lists. */
  valueRefTasks: ValueRefTask[];
  onAttributeMappingChange: (entries: { key: string; value: string }[]) => void;
  /**
   * Real workflows in this org, used to constrain the Static Target Chatbot to
   * an existing workflow code instead of free text. When empty (org not yet
   * resolved) the field falls back to the current value only.
   */
  availableWorkflows?: { short_code: string; title: string }[];
}

export const StartnewConfigSection = ({
  control,
  existingTasks,
  attributeMappingEntries,
  valueRefTasks,
  onAttributeMappingChange,
  availableWorkflows = [],
}: StartnewConfigSectionProps) => {
  return (
    <>
      <Divider />
      <Box>
        <VStack align="stretch" spacing={4}>
          <FormLabel
            color="text-primary"
            fontSize="md"
            fontWeight="medium"
            mb={0}
          >
            STARTNEW Configuration
          </FormLabel>
          <Text fontSize="sm" color="text-secondary">
            Configure how the target workflow is determined when starting a new session.
          </Text>

          <Box
            bg="bg-surface"
            p={4}
            rounded="md"
            borderWidth="1px"
            borderColor="border-default"
          >
            <VStack spacing={4} align="stretch">
              {/* Target Task Selection */}
              <FormControl>
                <FormLabel fontSize="sm" color="text-primary">
                  Target Task
                </FormLabel>
                <Text fontSize="xs" color="text-muted" mb={2}>
                  Select which task's value should be used to look up the target workflow from linked workflows.
                </Text>
                <Controller
                  name="startnewConfig.targetTask"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="Select a task"
                      bg="bg-surface"
                      borderColor="border-default"
                      size="sm"
                    >
                      {existingTasks
                        .filter(t => t.type === TaskType.CHOICE || t.type === TaskType.DYNAMIC_CHOICE || t.type === TaskType.TEXT)
                        .map((task) => (
                          <option key={task.id} value={task.index}>
                            {task.index}. {task.name}
                          </option>
                        ))}
                    </Select>
                  )}
                />
              </FormControl>

              {/* Default Target Key */}
              <FormControl>
                <FormLabel fontSize="sm" color="text-primary">
                  Default Target Key
                </FormLabel>
                <Text fontSize="xs" color="text-muted" mb={2}>
                  Fallback link key if the task value doesn't match any configured link.
                </Text>
                <Controller
                  name="startnewConfig.defaultTargetKey"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      placeholder="e.g., DEFAULT"
                      bg="bg-surface"
                      borderColor="border-default"
                      size="sm"
                      textTransform="uppercase"
                    />
                  )}
                />
              </FormControl>

              {/* Static Target Chatbot */}
              <FormControl>
                <FormLabel fontSize="sm" color="text-primary">
                  Static Target Chatbot
                </FormLabel>
                <Text fontSize="xs" color="text-muted" mb={2}>
                  Fallback workflow used when no linked workflow matches. Leave blank to restart the same workflow.
                </Text>
                <Controller
                  name="startnewConfig.targetChatbot"
                  control={control}
                  render={({ field }) => {
                    const currentValue = field.value ?? '';
                    const isStale =
                      currentValue !== '' &&
                      !availableWorkflows.some((wf) => wf.short_code === currentValue);
                    return (
                      <>
                        <Select
                          {...field}
                          value={currentValue}
                          onChange={(e) => field.onChange(e.target.value || undefined)}
                          placeholder="Same workflow (restart)"
                          bg="bg-surface"
                          borderColor="border-default"
                          size="sm"
                        >
                          {availableWorkflows.map((wf) => (
                            <option key={wf.short_code} value={wf.short_code}>
                              {wf.title} ({wf.short_code})
                            </option>
                          ))}
                          {/* Preserve a code that is no longer a known workflow */}
                          {isStale && (
                            <option value={currentValue}>{currentValue}</option>
                          )}
                        </Select>
                        {isStale && (
                          <Text fontSize="xs" color="orange.500" mt={1}>
                            "{currentValue}" is not a current workflow in this org — it may have been renamed or deleted.
                          </Text>
                        )}
                      </>
                    );
                  }}
                />
              </FormControl>

              {/* Attribute Mapping */}
              <FormControl>
                <HStack justify="space-between" mb={2}>
                  <Box>
                    <FormLabel fontSize="sm" color="text-primary" mb={0}>
                      Attribute Mapping
                    </FormLabel>
                    <Text fontSize="xs" color="text-muted">
                      Map values to pass to the target workflow, such as an answer from this one.
                    </Text>
                  </Box>
                  <IconButton
                    aria-label="Add attribute"
                    icon={<MdAdd />}
                    size="xs"
                    colorScheme="brand"
                    onClick={() => onAttributeMappingChange([...attributeMappingEntries, { key: '', value: '' }])}
                  />
                </HStack>

                {attributeMappingEntries.length > 0 && (
                  <VStack spacing={2} align="stretch">
                    {attributeMappingEntries.map((entry, index) => (
                      <HStack key={index} spacing={2} align="flex-start">
                        <Input
                          placeholder="Attribute key"
                          size="sm"
                          value={entry.key}
                          onChange={(e) => {
                            const newEntries = [...attributeMappingEntries];
                            newEntries[index] = { ...newEntries[index], key: e.target.value };
                            onAttributeMappingChange(newEntries);
                          }}
                          bg="bg-surface"
                          borderColor="border-default"
                          flex={1}
                        />
                        <Box flex={2} minW={0}>
                          <ValueRefPicker
                            value={entry.value}
                            onChange={(value) => {
                              const newEntries = [...attributeMappingEntries];
                              newEntries[index] = { ...newEntries[index], value };
                              onAttributeMappingChange(newEntries);
                            }}
                            tasks={valueRefTasks}
                            testId={`wfv4-config-startnew-mapping-value-${index}`}
                          />
                        </Box>
                        <IconButton
                          aria-label="Remove attribute"
                          icon={<MdDelete />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => {
                            const newEntries = attributeMappingEntries.filter((_, i) => i !== index);
                            onAttributeMappingChange(newEntries);
                          }}
                        />
                      </HStack>
                    ))}
                  </VStack>
                )}

                {attributeMappingEntries.length === 0 && (
                  <Text fontSize="xs" color="text-muted" fontStyle="italic">
                    No attribute mappings configured. Click + to add one.
                  </Text>
                )}
              </FormControl>
            </VStack>
          </Box>
        </VStack>
      </Box>
    </>
  );
};
